import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, FormControl, Select, MenuItem, InputLabel, useTheme, useMediaQuery, TablePagination } from '@mui/material';
import { Icon } from '@iconify/react';
import { getAuditLogsByUserId, AuditLogEntry } from '../utils/auditLog';
interface AuditLogDrawerProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
}
const AuditLogDrawer: React.FC<AuditLogDrawerProps> = ({
  open,
  onClose,
  userId,
  userName
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Load audit logs when drawer opens or userId changes
  useEffect(() => {
    if (open && userId) {
      const logs = getAuditLogsByUserId(userId);
      setAuditLogs(logs);
    }
  }, [open, userId]);
  // Filter logs
  const filteredLogs = auditLogs.filter(log => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!log.fieldName.toLowerCase().includes(query) && !log.oldValue.toLowerCase().includes(query) && !log.newValue.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Event type filter
    if (eventTypeFilter !== 'all' && log.eventType !== eventTypeFilter) {
      return false;
    }
    // Source filter
    if (sourceFilter !== 'all' && log.source !== sourceFilter) {
      return false;
    }
    return true;
  });
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'Create':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      case 'Edit':
        return {
          bg: 'rgba(14, 165, 233, 0.1)',
          color: '#0EA5E9'
        };
      case 'Sync':
        return {
          bg: 'rgba(139, 92, 246, 0.1)',
          color: '#8B5CF6'
        };
      case 'Status Change':
        return {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        };
      default:
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
    }
  };
  const getSourceColor = (source: string) => {
    switch (source) {
      case 'BO_USER':
        return {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        };
      case 'BANK_API':
        return {
          bg: 'rgba(6, 182, 212, 0.1)',
          color: '#06B6D4'
        };
      case 'SYSTEM':
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
      default:
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
    }
  };
  // Paginated logs
  const paginatedLogs = filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        md: 900
      },
      p: 0
    }
  }} data-id="element-122">
      <Box sx={{
      p: 3,
      borderBottom: '1px solid #E5E7EB'
    }} data-id="element-123">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-124">
          <Box data-id="element-125">
            <Typography variant="h6" data-id="element-126">Audit Log</Typography>
            {userName && <Typography variant="body2" color="text.secondary" data-id="element-127">
                {userName} - {filteredLogs.length} entries
              </Typography>}
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-128">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-129" />
          </IconButton>
        </Box>
        {/* Filters */}
        <Box sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        mt: 2
      }} data-id="element-130">
          <TextField size="small" placeholder="Search field, old value, new value..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{
          flex: 1,
          minWidth: 200
        }} InputProps={{
          startAdornment: <InputAdornment position="start" data-id="element-132">
                  <Icon icon="lucide:search" width={16} height={16} data-id="element-133" />
                </InputAdornment>
        }} data-id="element-131" />
          <FormControl size="small" sx={{
          minWidth: 150
        }} data-id="element-134">
            <InputLabel data-id="element-135">Event Type</InputLabel>
            <Select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)} label="Event Type" data-id="element-136">
              <MenuItem value="all" data-id="element-137">All Types</MenuItem>
              <MenuItem value="Create" data-id="element-138">Create</MenuItem>
              <MenuItem value="Edit" data-id="element-139">Edit</MenuItem>
              <MenuItem value="Sync" data-id="element-140">Sync</MenuItem>
              <MenuItem value="Status Change" data-id="element-141">Status Change</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{
          minWidth: 150
        }} data-id="element-142">
            <InputLabel data-id="element-143">Source</InputLabel>
            <Select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} label="Source" data-id="element-144">
              <MenuItem value="all" data-id="element-145">All Sources</MenuItem>
              <MenuItem value="BO_USER" data-id="element-146">BO User</MenuItem>
              <MenuItem value="BANK_API" data-id="element-147">Bank API</MenuItem>
              <MenuItem value="SYSTEM" data-id="element-148">System</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      overflowY: 'auto',
      height: 'calc(100vh - 280px)'
    }} data-id="element-149">
        <TableContainer component={Paper} variant="outlined" sx={{
        boxShadow: 'none'
      }} data-id="element-150">
          <Table size="small" data-id="element-151">
            <TableHead data-id="element-152">
              <TableRow data-id="element-153">
                <TableCell data-id="element-154">Timestamp</TableCell>
                <TableCell data-id="element-155">Event Type</TableCell>
                <TableCell data-id="element-156">Field Name</TableCell>
                <TableCell data-id="element-157">Old Value</TableCell>
                <TableCell data-id="element-158">New Value</TableCell>
                <TableCell data-id="element-159">Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-id="element-160">
              {paginatedLogs.length === 0 ? <TableRow data-id="element-161">
                  <TableCell colSpan={6} align="center" data-id="element-162">
                    <Box sx={{
                  py: 4
                }} data-id="element-163">
                      <Icon icon="lucide:inbox" width={48} height={48} color="#94A3B8" data-id="element-164" />
                      <Typography variant="body2" color="text.secondary" sx={{
                    mt: 2
                  }} data-id="element-165">
                        No audit logs found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow> : paginatedLogs.map(log => <TableRow key={log.id} data-id="element-166">
                    <TableCell data-id="element-167">
                      <Typography variant="body2" fontSize="0.8rem" data-id="element-168">
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-169">
                      <Chip label={log.eventType} size="small" sx={{
                  backgroundColor: getEventTypeColor(log.eventType).bg,
                  color: getEventTypeColor(log.eventType).color,
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }} data-id="element-170" />
                    </TableCell>
                    <TableCell data-id="element-171">
                      <Typography variant="body2" fontSize="0.8rem" fontWeight={500} data-id="element-172">
                        {log.fieldName}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-173">
                      <Typography variant="body2" fontSize="0.8rem" color="text.secondary" data-id="element-174">
                        {log.oldValue || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-175">
                      <Typography variant="body2" fontSize="0.8rem" fontWeight={500} data-id="element-176">
                        {log.newValue || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-177">
                      <Chip label={log.source} size="small" sx={{
                  backgroundColor: getSourceColor(log.source).bg,
                  color: getSourceColor(log.source).color,
                  fontSize: '0.7rem'
                }} data-id="element-178" />
                    </TableCell>
                  </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      backgroundColor: 'background.paper'
    }} data-id="element-179">
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredLogs.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-180" />
      </Box>
    </Drawer>;
};
export default AuditLogDrawer;